import * as Icons from "lucide-react";
import { Check, ShieldAlert, Sparkles } from "lucide-react";
import Section, { PageHero } from "@/components/Section";
import Reveal from "@/components/Reveal";
import CTAButton from "@/components/CTAButton";
import { FormNotice } from "@/components/FormControls";
import { foodStandards, painPoints } from "@/data/content";
import { useBrand } from "@/context/BrandContext";
import media from "@/config/media";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Sparkles;
  return <Cmp className={className} aria-hidden="true" />;
};

export default function FoodStandardsPage() {
  const brand = useBrand();
  const published = brand.claims.filter((c) => c.published);

  return (
    <>
      <PageHero
        eyebrow="Our food standards"
        title="What goes into every lunch matters."
        intro={foodStandards.intro}
      />

      <Section
        id="pillars"
        tone="creamDeep"
        eyebrow="Our five standards"
        title="The promises behind every box."
      >
        <div className="mt-12 space-y-5">
          {foodStandards.pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 70}>
              <div className="mbm-card mbm-card-hover grid gap-5 p-6 sm:grid-cols-[auto_1fr] sm:p-8">
                <div className="flex items-start gap-4 sm:flex-col sm:items-start">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-amber text-coralInk shadow-soft">
                    <Icon name={p.icon} className="h-5 w-5" />
                  </span>
                  <span className="font-heading text-2xl font-semibold text-coralInk/70 sm:mt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold sm:text-xl">{p.title}</h3>
                  <ul className="mt-3 space-y-2.5">
                    {p.points.map((pt) => (
                      <li key={pt.slice(0, 24)} className="flex gap-2.5 text-sm leading-relaxed text-cocoa/70">
                        <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-coralInk" aria-hidden="true" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section id="claims" eyebrow="What we can say today" title="Claims we stand behind">
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {published.map((claim, i) => (
            <Reveal key={claim.id} delay={i * 60}>
              <div className="mbm-card h-full p-5" data-testid={`claim-${claim.id}`}>
                <Check className="h-5 w-5 text-coralInk" aria-hidden="true" />
                <p className="mt-3 font-heading text-sm font-semibold leading-snug">{claim.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 max-w-2xl">
          <FormNotice tone="warn" testId="claims-note">
            A few claims are deliberately not published yet — preservative-free, cold-pressed oils,
            calorie-balanced meals and paediatrician approval. They will appear only once the
            recipes, suppliers and processes consistently support them, and any paediatrician
            endorsement will carry that professional’s name and qualifications.
          </FormNotice>
        </div>
      </Section>

      <Section id="standards-detail" tone="creamDeep">
        <div className="grid gap-12 lg:grid-cols-[1fr_0.8fr] lg:items-start">
          <div className="space-y-8">
            {foodStandards.sections.map((s, i) => (
              <Reveal key={s.title} delay={i * 80}>
                <h2 className="font-heading text-xl font-semibold">{s.title}</h2>
                <ul className="mt-4 space-y-2.5">
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-2.5 text-sm leading-relaxed text-cocoa/70">
                      <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-coralInk" aria-hidden="true" />
                      {p}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
          <Reveal delay={140}>
            <div className="overflow-hidden rounded-[32px] shadow-softLg">
              <img
                src={media.ingredients}
                alt="Fresh vegetables and lentils being prepared"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      <Section
        id="questions"
        tone="sage"
        eyebrow="Questions parents actually ask"
        title="We built the menu around these."
        intro="These are the questions that come up before a parent trusts anyone else with their child’s lunch. Each meal card answers most of them directly."
      >
        <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-2">
          {painPoints.map((q, i) => (
            <Reveal as="li" key={q} delay={i * 40}>
              <p className="rounded-control bg-surface shadow-soft px-5 py-4 font-heading text-sm font-medium text-cocoa">
                “{q}”
              </p>
            </Reveal>
          ))}
        </ul>
      </Section>

      <Section id="allergy" tone="forest">
        <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
          <Reveal>
            <ShieldAlert className="h-7 w-7 text-amber" aria-hidden="true" />
            <h2 className="mt-4 font-heading text-2xl font-semibold text-white sm:text-3xl">
              Allergy disclaimer
            </h2>
            <p className="mt-4 max-w-2xl text-sm font-medium leading-relaxed text-white sm:text-base">
              {foodStandards.allergyDisclaimer}
            </p>
          </Reveal>
          <Reveal delay={100}>
            <CTAButton to="/legal/allergy" variant="onDark" data-testid="allergy-policy-cta">
              Read the full disclaimer
            </CTAButton>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
